/**
 * The site the user is working at (web "site choice", like the C5 app). Remembered per tenant in
 * localStorage; screens use it as the default site filter and for scan batches. The API still
 * checks every request against the user's site scope.
 *
 *   const { siteId, setSiteId, sites } = useCurrentSite();
 */
import { useEffect, useSyncExternalStore } from 'react';
import { useOrgKey, useSites } from './queries';

const KEY = 'wms.site.';
const listeners = new Set<() => void>();
const read = (org: string): string | null => {
  try {
    return localStorage.getItem(KEY + org);
  } catch {
    return null;
  }
};

export function setCurrentSite(org: string, siteId: string | null): void {
  try {
    if (siteId) localStorage.setItem(KEY + org, siteId);
    else localStorage.removeItem(KEY + org);
  } catch {
    /* private mode */
  }
  listeners.forEach((l) => l());
}

export function useCurrentSite() {
  const org = useOrgKey();
  const sites = useSites();
  const stored = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => read(org),
  );
  const valid = sites.data?.some((s) => s.id === stored) ? stored : null;
  const siteId = valid ?? (sites.data?.length === 1 ? sites.data[0].id : null);
  useEffect(() => {
    if (stored && sites.data && !valid) setCurrentSite(org, null);
  }, [stored, valid, sites.data, org]);
  return { siteId, site: sites.data?.find((s) => s.id === siteId) ?? null, sites: sites.data ?? [], loading: sites.isLoading, setSiteId: (id: string | null) => setCurrentSite(org, id) };
}
